import React from 'react';
import PropTypes from 'prop-types';
import Question from '../../common/Question';
import TextAreaInput from '../../common/TextAreaInput';
import YesNoInputGroup from '../../common/YesNoInputGroup';
import './ExpertiseTab.scss';

const isDescEmpty = (desc) => !desc || !desc.trim();
const isDescTooShort = (desc) => !desc || desc.replaceAll(/ /g, '').length < 50;

const exampleText = {
  therapeutic_areas: {
    positive: 'While at Genzyme worked on Endocrine Ph III study and at Cubist worked on Ph I Metabolism study.',
    negative: 'I do not have experience in cardiology but have worked with Class III devices in the pulmonary/respiratory space.',
  },
  disease_codes: {
    positive: 'I designed a Phase II protocol for triple negative breast cancer while at Roche.',
    negative: `I have not worked with sickle cell anemia specifically but have a strong understanding of blood diseases
and immune-mediated hematological disorders, as I worked for Celgene.`,
  },
  regulatory_pathways: {
    positive: 'I have worked on 10+ IND studies and 4 BLA studies throughout my career across a range of disease areas.',
    negative: 'I have supported clinical trials from phase 2b and 3 as well as post marketing but have not been directly involved with writing INDs.',
  },
  therapies: {
    positive: 'I have held several CMC positions for small molecules at Pfizer and J&J.',
    negative: 'I do not have experience in In Vitro Diagnostics using biological samples. I do have experience developing micro-electronics for biotech applications.',
  },
  about: `[Project role] with [#] years of experience working for [small/medium/large/enterprise 
biotechnology/pharmaceutical/medical device/CRO] companies. With [list your top accomplishments] and expertise in 
[list Therapeutic Area(s) and/or Product Technologies/Modalities needed for the project that match your background], 
I am very interested in this project. Based on my experience, I believe that I would be a terrific fit.`,
};

const renderRequired = (render) => {
  if (render) {
    return (
      <div className="body-text-2 medium-weight with-error-text mb-2">
        <i className="fa fa-exclamation-triangle" />
        {' '}
        Required!
      </div>
    );
  }
  return null;
};

function ExpertiseTab({
  project,
  proposal,
  onFormChange,
  errors,
  screeningAnswers,
}) {
  const numberIndexes = ['1. ', '2. ', '3. '];
  const letterIndexes = ['a. ', 'b. ', 'c. ', 'd. ', 'e. ', 'f. '];

  const tagTypes = ['therapeutic_areas', 'disease_codes', 'regulatory_pathways', 'therapies'];
  const tagHeadings = {
    therapeutic_areas: 'Therapeutic Areas',
    disease_codes: 'Diseases & Indications',
    regulatory_pathways: 'Regulatory Pathways',
    therapies: 'Product Technologies',
  };
  const displayExperienceQuestion = tagTypes.reduce((aggr, tagType) => (
    aggr || project[tagType].length !== 0
  ), false);

  const displayedErrors = {};
  const displayedExample = {};

  tagTypes.forEach((category) => {
    if (isDescEmpty(proposal[`${category}_desc`])) {
      displayedErrors[category] = errors[`${category}_desc`];
    }
    if (proposal[category]) {
      displayedExample[category] = proposal[category].some((p) => p.selected === 'No') ? exampleText[category].negative : exampleText[category].positive;
    }
  });

  screeningAnswers.forEach((answer) => {
    if (isDescTooShort(answer.answer_description)) {
      displayedErrors[`screening_answers_${answer.id}`] = errors[`screening_answers_${answer.id}`];
    }
  });

  if (isDescEmpty(proposal.about_consultant)) {
    displayedErrors.about_consultant = errors.about_consultant;
  }

  return (
    <div>
      { screeningAnswers.length
        ? (
          <div className="body-text-2 medium-weight mb-5">
            { numberIndexes.shift() }
            Your experience related to project responsibilities
            <ul className="screening-questions-list">
              { screeningAnswers.map((q) => (
                <li>
                  <Question
                    title={q.question_text}
                    location="proposal-edit"
                  >
                    <TextAreaInput
                      className="project-screening-question"
                      formKey={`screening_answer_${q.id}`}
                      onAnswerChange={(val) => onFormChange(`screening_answer_${q.id}`, val)}
                      errors={errors}
                      value={proposal[`screening_answer_${q.id}`]}
                      textPromptClass={displayedErrors[`screening_questions_${q.question_id}`] && 'with-error-text'}
                      textAreaPrompt="Minimum 50 characters."
                    />
                  </Question>
                </li>
              ))}
            </ul>
          </div>
        ) : ''}

      { displayExperienceQuestion
        && (
        <div>
          <div className="body-text-2 medium-weight mb-5">
            { numberIndexes.shift() }
            Is your profile reflecting your experience correctly?
          </div>

          { tagTypes.map((tagType) => (
            (project[tagType].length !== 0)
            && (
            <div className="ms-4 mb-5">
              <Question
                title={`${letterIndexes.shift()}${tagHeadings[tagType]}`}
                location="proposal-edit"
              >
                <div className="ms-4">
                  <YesNoInputGroup
                    options={project[tagType]}
                    formKey={tagType}
                    value={proposal[tagType]}
                    onAnswerChange={(val) => onFormChange(tagType, val)}
                    errors={errors}
                  />
                  { proposal[tagType]
                    && proposal[tagType].some((tag) => tag.selected !== null)
                    && (
                    <div className={`mt-3 ${tagType.replace(/_/, '-')}-description`}>
                      {renderRequired(displayedErrors[tagType])}
                      <TextAreaInput
                        placeholder="Type your experience here."
                        onAnswerChange={(val) => onFormChange(`${tagType}_desc`, val)}
                        value={proposal[`${tagType}_desc`]}
                        error={displayedErrors[tagType]}
                        exampleText={displayedExample[tagType]}
                        textPromptClass={displayedErrors[tagType] && 'with-error-text'}
                        textAreaPrompt="Tell us specific examples of your experience as it relates
                        to the answers above."
                        required={null}
                      />
                    </div>
                    )}
                </div>
              </Question>
            </div>
            )
          ))}
        </div>
        )}
    </div>
  );
}

ExpertiseTab.propTypes = {
  project: PropTypes.shape({}).isRequired,
  screeningAnswers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  proposal: PropTypes.shape({}).isRequired,
  onFormChange: PropTypes.func.isRequired,
  errors: PropTypes.shape({}).isRequired,
};

ExpertiseTab.defaultProps = {
};

export default ExpertiseTab;
